/**
 * Hook for lazy-loading scriptBlocks from Cloud Storage
 * 
 * ScriptBlocks are stored in Cloud Storage (gzipped) to avoid Firestore's
 * 1MB document limit. This hook loads them on-demand when the Script Viewer
 * or other script-dependent features are accessed.
 */

import { useState, useEffect, useRef } from 'react';
import { useAppStore } from '../../../store/useAppStore';
import {
    loadScriptImportArtifacts,
    type ClassifiedStructure,
} from '../../sync/script-storage-api';
import type { ScriptBlock } from '../types';
import { mergeExtractedProps, mergeExtractedSoundCues } from '../utils/mergeImportedProductionElements';
import { linkBlocksToStructure } from '../utils/deriveBlockLinks';
import {
    mapClassifiedCharacters,
    mapClassifiedScenes,
    mergeClassifiedCharacterDetails,
} from '../utils/classified-structure';

// ============================================
// Helper functions (pure logic, no hooks)
// ============================================

function hasUsableStoredStructure(): boolean {
    const state = useAppStore.getState();
    const liveScenes = new Set(
        state.showStructure.scenes
            .filter(scene => !scene.deleted)
            .map(scene => scene.id)
    );

    return state.showStructure.acts
        .filter(act => !act.isPreset && !act.deleted)
        .some(act => (act.sceneIds || []).some(sceneId => liveScenes.has(sceneId)));
}

function needsCharacterRehydration(): boolean {
    const state = useAppStore.getState();
    const characters = state.showStructure.characters;
    if (characters.length === 0) return true;

    const activeCharacters = characters.filter(character => !character.deleted);
    if (activeCharacters.length === 0) return false;

    return activeCharacters.every(character => !character.sceneIds || character.sceneIds.length === 0);
}

function linkLoadedScriptBlocks(
    blocks: ScriptBlock[],
    classifiedStructure: ClassifiedStructure | null
): ScriptBlock[] {
    if (classifiedStructure) {
        return linkBlocksToStructure(
            blocks,
            classifiedStructure.acts || [],
            mapClassifiedScenes(classifiedStructure.scenes),
            mapClassifiedCharacters(classifiedStructure.characters)
        );
    }

    const { showStructure } = useAppStore.getState();
    return linkBlocksToStructure(
        blocks,
        showStructure.acts,
        showStructure.scenes,
        showStructure.characters
    );
}

/** Apply classified structure or fall back to client-side derivation. */
function applyStructure(classifiedStructure: ClassifiedStructure | null): void {
    const actions = useAppStore.getState().showStructureActions;
    if (classifiedStructure) {
        actions.applyClassifiedStructure(classifiedStructure);
        if (!classifiedStructure.characters || classifiedStructure.characters.length === 0) {
            actions.deriveCharactersFromBlocks();
        }
    } else {
        actions.deriveStructureFromBlocks();
        actions.deriveCharactersFromBlocks();
    }
}

/**
 * Cloud Storage holds the original AI import result. Firestore showStructure is
 * the editable source of truth after import, so storage rehydration may fill
 * missing data but must not overwrite already-saved act/scene edits.
 */
function rehydrateMissingStructure(classifiedStructure: ClassifiedStructure | null): void {
    const actions = useAppStore.getState().showStructureActions;

    if (!hasUsableStoredStructure()) {
        applyStructure(classifiedStructure);
        return;
    }

    if (!needsCharacterRehydration()) return;

    if (classifiedStructure?.characters?.length) {
        const currentCharacters = useAppStore.getState().showStructure.characters;
        const nextCharacters = currentCharacters.length === 0
            ? mapClassifiedCharacters(classifiedStructure.characters)
            : mergeClassifiedCharacterDetails(currentCharacters, classifiedStructure.characters);
        if (nextCharacters !== currentCharacters) {
            actions.setCharacters(nextCharacters);
        }
    } else if (useAppStore.getState().showStructure.characters.length === 0) {
        actions.deriveCharactersFromBlocks();
    }
}

/** Check if structure needs rehydration from classified data */
function needsStructureRehydration(): boolean {
    return !hasUsableStoredStructure() || needsCharacterRehydration();
}

// ============================================
// Hook
// ============================================

export interface UseScriptBlocksResult {
    isLoading: boolean;
    error: string | null;
    hasLoaded: boolean;
}

export function useScriptBlocks(enabled = true): UseScriptBlocksResult {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasLoaded, setHasLoaded] = useState(false);
    const fetchRef = useRef<{ scopeKey: string; includeScriptBlocks: boolean } | null>(null);
    
    const cloudEnabled = useAppStore(state => state.cloud.enabled);
    const cloudOrgId = useAppStore(state => state.cloud.orgId);
    const cloudProjectId = useAppStore(state => state.cloud.projectId);
    const scriptTextAccess = useAppStore(state => state.cloud.scriptTextAccess ?? false);
    const activeJobId = useAppStore(state => state.activeJobId);
    const hasScriptBlocks = useAppStore(state => (state.showStructure.scriptBlocks?.length ?? 0) > 0);
    
    useEffect(() => {
        // Skip if not cloud-enabled or no project
        if (!enabled || !cloudEnabled || !cloudOrgId || !cloudProjectId) return;
        const scope = { orgId: cloudOrgId, projectId: cloudProjectId };
        const scopeKey = `${cloudOrgId}/${cloudProjectId}`;
        if (fetchRef.current?.scopeKey !== scopeKey) {
            fetchRef.current = null;
            setHasLoaded(false);
        }

        // CRITICAL: If a job is active, do NOT load from storage (data is stale/updating)
        if (activeJobId) return;
        
        // If blocks exist in store, check if structure needs rehydration
        if (hasScriptBlocks) {
            setHasLoaded(true);
            if (needsStructureRehydration()) {
                void (async () => {
                    try {
                        const { classifiedStructure } = await loadScriptImportArtifacts(scope, {
                            includeScriptBlocks: false,
                            includeProductionElements: false,
                        });
                        rehydrateMissingStructure(classifiedStructure);
                    } catch (error: unknown) {
                        console.warn('[useScriptBlocks] Rehydration failed:', error);
                    }
                })();
            }
            return;
        }
        
        const previousFetch = fetchRef.current;
        if (previousFetch) {
            const needsScriptTextRetry = scriptTextAccess
                && !previousFetch.includeScriptBlocks
                && !hasScriptBlocks;
            if (!needsScriptTextRetry) return;
        }
        
        const loadAll = async () => {
            const includeScriptBlocks = scriptTextAccess;
            fetchRef.current = { scopeKey, includeScriptBlocks };
            setIsLoading(true);
            setError(null);
            
            try {
                const {
                    scriptBlocks: blocks,
                    classifiedStructure,
                    extractedProps,
                    extractedSoundCues,
                } = await loadScriptImportArtifacts(scope, {
                    includeScriptBlocks,
                });
                
                // Apply blocks
                if (blocks.length > 0) {
                    useAppStore.getState().showStructureActions.setScriptBlocks(
                        linkLoadedScriptBlocks(blocks, classifiedStructure)
                    );
                }
                
                // Rehydrate only missing structure; Firestore remains the editable source of truth.
                rehydrateMissingStructure(classifiedStructure);
                
                // Merge production elements
                mergeExtractedProps(extractedProps);
                mergeExtractedSoundCues(extractedSoundCues);
                
                setHasLoaded(true);
            } catch (error: unknown) {
                console.error('[useScriptBlocks] Load failed:', error);
                setError(error instanceof Error ? error.message : 'Failed to load script');
                fetchRef.current = null;
            } finally {
                setIsLoading(false);
            }
        };
        
        void loadAll();
    }, [enabled, cloudEnabled, cloudOrgId, cloudProjectId, hasScriptBlocks, activeJobId, scriptTextAccess]);
    
    return { isLoading, error, hasLoaded };
}
